import { CSSResultGroup, TemplateResult, html } from 'lit';
import {
  OutlineElement,
  SlotsController,
  LightDomStyles,
} from '@phase2/outline-core';
import { customElement, property, queryAssignedNodes } from 'lit/decorators.js';
import componentStyles from './outline-hero.css.lit';
import componentLightDomStyles from './outline-hero-light-dom.css.lit';
import { classMap } from 'lit/directives/class-map.js';

/**
 * The OutlineHero component
 * @element outline-hero
 * @slot heading - For outline-heading component
 * @slot description - For outline-styled-text component
 * @slot default for image/video
 */
@customElement('outline-hero')
export class OutlineHero extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  slots = new SlotsController(this);
  resizeObserver: ResizeObserver;

  lightDomStyles = new LightDomStyles(
    this,
    componentLightDomStyles,
    this.tagName.toLowerCase()
  );

  /**
   * Document property here
   */
  @property({ type: Boolean, attribute: 'has-background' })
  hasBackground: boolean;

  /**
   * Document property here
   */
  @property({ type: Boolean, attribute: 'has-fixed-height' })
  hasFixedHeight: boolean;

  /**
   * Aspect ratio of Hero component.
   */
  @property({ type: String, reflect: true, attribute: 'aspect-ratio' })
  aspectRatio: string;

  /**
   * Override the default opacity of the background image.
   */
  @property({ type: String, reflect: true, attribute: 'level-opacity' })
  levelOpacity: string;

  /**
   * Background image slot.
   */
  @queryAssignedNodes({ slot: 'background', flatten: true })
  backgroundSlot: HTMLElement[];

  connectedCallback() {
    super.connectedCallback();
    setTimeout(() => this.checkForBackground(), 300);
    setTimeout(() => this.checkForFixedHeight(), 300);
  }

  render(): TemplateResult {
    return html`
      <section class=${this.createClassMap('outline-hero')}>
        ${this.hasBackground
          ? html` <div class="overlay overlay--${this.levelOpacity}"></div> `
          : null}
        <div class="outline-hero--content">
          <div class=${this.createClassMap('outline-hero--heading')}>
            ${this.slots.conditionalSlot('heading', false)}
          </div>
          <div class=${this.createClassMap('outline-hero--description')}>
            ${this.slots.conditionalSlot('description', false)}
          </div>
        </div>
        <div class="outline-hero--background">
          ${this.slots.conditionalSlot('background', false)}
        </div>
      </section>
    `;
  }

  createClassMap(classname: string) {
    return classMap({
      [classname]: true,
      'has-no-background': !this.hasBackground,
      'has-fixed-height': this.hasFixedHeight,
      [`aspect-ratio-${this.aspectRatio}`]: this.aspectRatio,
    });
  }

  checkForBackground() {
    const hasBackground = !!this.backgroundSlot.length;
    hasBackground
      ? (this.setAttribute('has-background', 'true'), this.applyResizeHandler())
      : null;
    this.applyResizeHandler();
  }

  checkForFixedHeight() {
    const hasFixedHeight = this.getAttribute('has-fixed-height');
    hasFixedHeight === 'true'
      ? this.setAttribute('has-fixed-height', 'true')
      : this.removeAttribute('has-fixed-height');
    this.applyResizeHandler();
  }

  applyResizeHandler() {
    this.resizeObserver = new ResizeObserver(() => this.setHeightVar());

    const headingTextContent = this.shadowRoot?.querySelector(
      '.outline-hero--heading'
    ) as HTMLElement;
    const descriptionTextContent = this.shadowRoot?.querySelector(
      '.outline-hero--description'
    ) as HTMLElement;

    this.resizeObserver.observe(headingTextContent);
    this.resizeObserver.observe(descriptionTextContent);
  }

  setHeightVar() {
    const headingTextContent = this.shadowRoot?.querySelector(
      '.outline-hero--heading'
    ) as HTMLElement;

    const descriptionTextContent = this.shadowRoot?.querySelector(
      '.outline-hero--description'
    ) as HTMLElement;

    this.style.setProperty(
      '--hero-background--min-height',
      !this.hasFixedHeight
        ? `${
            headingTextContent?.clientHeight +
            descriptionTextContent?.clientHeight +
            32
          }px`
        : '240px'
    );
  }
}
